import React, {useEffect, useState, useContext} from "react";
import {nfmsPost} from "../../handler/NfmsClient";
import {FormattedMessage, injectIntl} from "react-intl";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column"; 
import Select, { SelectOption } from "@amzn/meridian/select";
import Input from "@amzn/meridian/input"
import Text from "@amzn/meridian/text"
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert"
import {getErrorAlertObjectFromResponse, getNodeId} from "../../Utils";
import {fetchAllPrograms, fetchFeaturesForProgram, fetchCycleConfigs} from "../../NetworkUtils";
import {ALERT_TYPES, GRANULARITY_LEVELS} from "../../Constants";
import PageHeader from "../util/PageHeader";
import {NO_PERMISSION_ERROR, PAGE_HEADERS} from "../util/RouteConstants";
import {getUserLogin, PermissionsContext} from "../permissions/PermissionContext";
import ErrorPage from "../error/ErrorPage";
import Tag from "@amzn/meridian/tag";


const EnableProgram = () => {
    // Checking editor permission
    const {canEnableProgram} = useContext(PermissionsContext);

    const [programName, setProgramName] = useState();
    const [isNumeric, setIsNumeric] = useState(false);
    const [mcmId, setMcmId] = useState();
    const [numericValue, setNumericValue] = useState();
    const [sortType, setSortType] = useState();
    const [cycles, setCycles] = useState([]);
    const [allCycleConfigs, setAllCycleConfigs] = useState([]);
    const [availableSortTypes, setAvailableSortTypes] = useState([]);
    const [availableCycles, setAvailableCycles] = useState([]);
    const [hasCycleLevelFeatures, setHasCycleLevelFeatures] = useState(false);
    const [alert, setAlert] = useState({visible: false, text:"", type:""})
    const [programList, setProgramList] = useState([]);
    const warningAlert = {visible: true, text: "Enable Program request received.", type:ALERT_TYPES.WARNING};
    const successAlert = {visible: true, text: "Successfully enabled Program", type:ALERT_TYPES.SUCCESS};

    const nodeId = getNodeId();

    useEffect(() => {
        fetchAllPrograms().then(response => setProgramList(response))
    }, []);

    useEffect(() => {
        if (hasCycleLevelFeatures && nodeId) {
            fetchCycleConfigs(nodeId).then(response => {
                setAllCycleConfigs(response);
                // Derive unique sort types from the cycle configs
                const sortTypes = [...new Set(response.map(config => config.sortType))];
                setAvailableSortTypes(sortTypes);
            });
        } else {
            setAllCycleConfigs([]);
            setAvailableSortTypes([]);
            setAvailableCycles([]);
            setSortType(null);
            setCycles([]);
        }
    }, [hasCycleLevelFeatures]);

    useEffect(() => {
        if (sortType && allCycleConfigs.length > 0) {
            // Filter cycles that match the selected sort type
            const filteredCycles = allCycleConfigs
                .filter(config => config.sortType === sortType)
                .map(config => config.cycleName);
            setAvailableCycles(filteredCycles);
        } else {
            setAvailableCycles([]);
        }
        setCycles([]);
    }, [sortType]);

    if (canEnableProgram === false) {
        return <ErrorPage errorTitle="Something went wrong" errorMessage={NO_PERMISSION_ERROR} />;
    }

    const programOnChange = (programName) => {
        /*
        * On change of select dropdown of programs, we are getting features of the program
        * If feature has numeric attribute then we will show numeric value input
        * */
        clearData();
        setProgramName(programName)
        if(programName) {
            fetchFeaturesForProgram(programName
            ).then(response => {
                if(response) {
                    setIsNumeric(response.some(feature => feature.isNumeric));
                    setHasCycleLevelFeatures(response.some(feature => feature.granularityLevel === GRANULARITY_LEVELS.CYCLE));
                }
            });
        }

    }

    // For cycle-level programs, selecting at least one cycle is mandatory.
    // This prevents enabling with an empty cycle set (which would create an empty placeholder entry).
    const isCycleSelectionMissing = hasCycleLevelFeatures && cycles.length === 0;

    function enableProgram () {
        if (isCycleSelectionMissing) {
            setAlert({visible: true, text: "Please select at least one cycle to enable this program.", type: ALERT_TYPES.ERROR});
            return;
        }
        setAlert(warningAlert);
        const params = { program: programName, nodeId: nodeId, mcmId: mcmId};
        if (numericValue && isNumeric) {
            // We only want to send numeric value if we have numeric config and numeric value is entered
            params.numericValue = parseInt(numericValue, 10)
        }
        if (cycles.length > 0) {
            params.cycles = cycles;
        }
        (async ()=>{
                        const userAlias = await getUserLogin();
            params.userAlias = userAlias;
            nfmsPost("EnableProgram", params)
                .then((response) => {
                    if (response.status === 200 && response.data.isSuccess === true) {
                        setAlert(successAlert);
                    }
                })
                .catch((response) => {
                    setAlert(getErrorAlertObjectFromResponse(response));
                })
        })();
    }

    const clearData = () => {
        setIsNumeric(false);
        setNumericValue(null);
        setSortType(null);
        setCycles([]);
        setAllCycleConfigs([]);
        setAvailableSortTypes([]);
        setAvailableCycles([]);
        setHasCycleLevelFeatures(false);
    }

    return (
        <div className="App">
            <Column spacingInset={"small large"}>
                <Row widths={["grid-12"]} alignmentVertical="top" >
                    <PageHeader {...PAGE_HEADERS.ENABLE_PROGRAM} />
                </Row>
                <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                    <Column spacing="200" alignmentHorizontal="left">
                        <Text type="b200" color="secondary">
                            <FormattedMessage id="enableProgram.programName" defaultMessage="Program Name"/>
                        </Text>
                        <Select value={programName} onChange={programOnChange} placeholder="Select Program Name" width={400}>
                            {programList.map(program => (
                                <SelectOption label={program} value={program} key={program} />
                            ))}
                        </Select>
                    </Column>
                </Row>

                <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                    <Column spacing="200" alignmentHorizontal="left">
                        <Text type="b200" color="secondary">
                            <FormattedMessage id="enableProgram.mcm" defaultMessage="MCM Id"/>
                        </Text>
                        <Input id="mcm-input" value={mcmId} onChange={setMcmId} type="text" placeholder="Enter MCM Id" width={400}/>
                    </Column>
                </Row>

                <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                    <Column spacing="200" alignmentHorizontal="left">
                        <Text type="b200" color="secondary">
                            <FormattedMessage id="enableProgram.nodeId" defaultMessage="Node Id"/>
                        </Text>
                        <Input id="nodeId-input" disabled={true} value={nodeId} type="text" width={400}/>
                    </Column>
                </Row>

                { isNumeric &&
                    <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                    <Column spacing="200" alignmentHorizontal="left">
                        <Text type="b200" color="secondary">
                            <FormattedMessage id="enableProgram.numericValue" defaultMessage="Numeric Value"/>
                        </Text>
                        <Input id="numericValue-input" value={numericValue} onChange={setNumericValue} type="text"  placeholder="Enter Numeric Value" width={400}/>
                    </Column>
                </Row>
                }

                { hasCycleLevelFeatures &&
                    <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                        <Column spacing="200" alignmentHorizontal="left">
                            <Text type="b200" color="secondary">
                                <FormattedMessage id="enableProgram.sortType" defaultMessage="Sort Type"/>
                            </Text>
                            <Select value={sortType} onChange={setSortType} placeholder="Select Sort Type" width={400}>
                                {availableSortTypes.map(type => (
                                    <SelectOption label={type.replace(/_/g, ' ')} value={type} key={type} />
                                ))}
                            </Select>
                        </Column>
                    </Row>
                }

                { hasCycleLevelFeatures && sortType &&
                    <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                        <Column spacing="200" alignmentHorizontal="left">
                            <Text type="b200" color="secondary">
                                <FormattedMessage id="enableProgram.cycles" defaultMessage="Cycles"/>
                            </Text>
                            <Select value={cycles} onChange={setCycles} placeholder="Select Cycles" width={400}>
                                {availableCycles.map(cycle => (
                                    <SelectOption label={cycle} value={cycle} key={cycle} />
                                ))}
                            </Select>
                        </Column>
                    </Row>
                }

                { hasCycleLevelFeatures && cycles.length > 0 &&
                    <Row alignmentVertical="top" spacingInset="none none medium none" wrap="down">
                        {cycles.map(cycle => (
                            <Tag type="neutral" key={cycle} onClose={() => setCycles(cycles.filter(c => c !== cycle))}>
                                {cycle}
                            </Tag>
                        ))}
                    </Row>
                }

                <Row alignmentVertical="top" spacingInset="large large large none">
                    <Button onClick={() => enableProgram()} disabled={isCycleSelectionMissing}>
                        <FormattedMessage id="enableProgram.enable" defaultMessage="Enable Program"/>
                    </Button>
                </Row>
                <Row alignmentVertical="top" spacingInset="none none medium none">
                    {alert.visible ? <Alert type={alert.type}>{alert.text}</Alert> : null}
                </Row>
            </Column>
        </div>
    );
}

export default injectIntl(EnableProgram);




// WEBPACK FOOTER //
// ./src/components/enable-program/EnableProgram.js